import * as React from 'react';
import ReactDOM from 'react-dom';
import { DocuSignConnectCard } from 'sma.common/components/react/components/DocuSign';
import InlineAlertCard from 'sma.common/components/react/components/InlineAlertCard';
import docusignConnect from 'sma.common/docusign-connect';
import getComponentWithTheme from '../../../../themeProvider';

// Fields is passed in by fields.js after TextField is defined — avoids a
// circular dependency (TextField lives in fields.js, not base-field.js).
function createDocuSignField(Fields, chide, Option) {
	return $.klass(Fields.TextField, {
		type: 'docusign-signature',
		qlass: 'docusign-signature',
		cssType: 'docusign-signature',
		displayName: gettext('Docusign'),
		isEnabled: true,
		helpText: gettext('Envelope usage is managed in your Docusign account. Ensure you have sufficient quota for your expected volume.'),
		isBranchable: false,
		isCustom: true,

		// A signature isn't a number or set of choices, so it can't sensibly drive a loop count.
		supportedActions: Fields.TextField.prototype.supportedActions.filter(
			(action) => action !== Fields.Actions.looping,
		),

		options: $.extend({}, Fields.TextField.prototype.options),

		initialize($super, info, editor) {
			$super(info || {}, editor);
			this._mountNode = null;
			this._othersBound = false;
			this._saveBlocked = false;
		},

		_bindOtherFieldListeners() {
			if (this._othersBound || !this.editor || !this.editor.survey) return;
			this._othersBound = true;

			const rerender = () => {
				if (this._saveBlocked && this._hasOtherQuestions()) {
					this._saveBlocked = false;
				}
				this._renderPanel();
			};
			this.editor.survey.pages.forEach((page) => {
				page.bind('fieldAdded', rerender);
				page.bind('fieldRemoved', rerender);
			});
		},

		showSaveBlockedBanner() {
			this._saveBlocked = true;
			this._renderPanel();
		},

		hideSaveBlockedBanner() {
			if (this._saveBlocked) {
				this._saveBlocked = false;
				this._renderPanel();
			}
		},

		_hasOtherQuestions() {
			if (!this.editor || !this.editor.survey) return true;
			return this.editor.survey.pages.some(
				(page) => page.fields.some((field) => field.type !== 'docusign-signature'),
			);
		},

		showOptions($super, options) {
			$super(options);
			options.html('');
			// Only show General options (Title, Description, Identifier).
			// Display options (side-by-side, append to previous, width) are excluded —
			// they don't apply to a signature block and can produce broken layouts.
			options.append(Option.renderGroup(gettext('General'), [this.name, this.title, this.description], false));
		},

		serialize($super) {
			const info = $super();
			info.children = [{ type: 'string' }];
			return info;
		},

		getChoiceContainer($super) {
			$super();
		},

		_renderPanel(programConnected, programAccountLabel) {
			if (!this._mountNode) return;

			const connectUrl = docusignConnect.getConnectUrl();
			const state = docusignConnect.getState
				? docusignConnect.getState()
				: { connected: false, accountLabel: '', error: null };
			const errorCopy = docusignConnect.getErrorCopy(state.error);
			const isConnected = programConnected !== undefined ? programConnected : !!state.connected;
			const accountLabel = programConnected !== undefined ? (programAccountLabel || '') : (state.accountLabel || '');
			// Starting a NEW connection is plan-gated — an already-connected site keeps
			// working regardless of plan (existing questions/connections aren't affected).
			const planBlocksConnect = !isConnected && this.editor && this.editor.docusignEnabled === false;

			ReactDOM.render(
				getComponentWithTheme(
					<>
						{planBlocksConnect ? (
							<InlineAlertCard
								title={gettext("This feature isn't available with your current plan. Upgrade to continue")}
								color='error'
							/>
						) : (
							<DocuSignConnectCard
								isConnected={isConnected}
								accountLabel={accountLabel}
								connectUrl={connectUrl}
							/>
						)}
						{errorCopy && (
							<InlineAlertCard title={errorCopy} color='error' />
						)}
						{this._saveBlocked && (
							<InlineAlertCard
								title={gettext('This question needs another question in the form')}
								description={gettext('Add another question before saving.')}
								color='error'
							/>
						)}
					</>,
					window.LOCALE && window.LOCALE.theme,
				),
				this._mountNode,
			);
		},

		renderContent($super) {
			// Field.render() calls this.element.empty() before renderContent() runs on every
			// re-render (e.g. any option change), which detaches _mountNode from the DOM even
			// though the reference itself stays truthy — so re-check isConnected, not just truthiness.
			if (!this._mountNode || !this._mountNode.isConnected) {
				if (this._mountNode) {
					ReactDOM.unmountComponentAtNode(this._mountNode);
				}
				this._mountNode = document.createElement('div');
				this._mountNode.className = 'docusign-react-mount';
				this.element.append(this._mountNode);
			}
			this._bindOtherFieldListeners();
			this._renderPanel();

			const awardId = window.sma_award_id;
			const connectionsUrl = window.DOCUSIGN_CONNECTIONS_URL;
			if (awardId && connectionsUrl) {
				fetch(`${connectionsUrl}?award_id=${awardId}`, { credentials: 'same-origin' })
					.then((r) => (r.ok ? r.json() : Promise.reject()))
					.then((data) => {
						const list = data.connections || [];
						const currentId = data.current_connection_id;
						const current = currentId != null ? list.find((c) => c.id === currentId) : null;
						this._renderPanel(
							!!current,
							current ? (current.display_name || current.account_email || '') : '',
						);
					})
					.catch(() => {
						docusignConnect.fetchStatus().then(() => this._renderPanel()).catch(() => {});
					});
			} else {
				docusignConnect.fetchStatus().then(() => this._renderPanel()).catch(() => {});
			}
		},

		destroy($super) {
			if (this._mountNode) {
				ReactDOM.unmountComponentAtNode(this._mountNode);
				this._mountNode = null;
			}
			$super();
		},
	});
}

module.exports = { createDocuSignField };
